import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import { Redirect, Route, Switch, useRouteMatch } from '@react-router';
import { Flex, TextV2 } from '@withjoy/joykit';
import type { MediaCollectionProps } from './MediaCollection.types';
import { pxToRem } from '@withjoy/joykit/theme';
import type { MediaCollectionEventInfoQuery } from './MediaCollection.graphql.generated';
import { useMediaCollectionEventInfoQuery, useMediaCollectionByMagicTokenForUpdateQuery } from './MediaCollection.graphql.generated';
import { EventDesignPurpose } from '@graphql/generated-base';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { MediaCollectionRouter, useRoutePaths } from './MediaCollection.routes';
import { MediaCollectionHead } from './MediaCollectionHead';
import { MediaCollectionTelemetryProvider } from './MediaCollection.telemetry';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { display5ToEventTitleOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { EventDesignThemingProvider, useEventDesignThemingProvider } from '@shared/core/website/theming';
import { EventDesignJoyKitThemeOverridesProvider } from '@shared/core/website/theming/EventDesignJoyKitThemeOverridesProvider';
import { usePrepareFilestackForUse } from '@shared/utils/filestack';

import { LazyAlbumsList } from './routes/AlbumsList/AlbumsList.lazy';
import { LazyUploadAlbum } from './routes/UploadAlbum/UploadAlbum.lazy';
import { LazyViewAlbum } from './routes/ViewAlbum/ViewAlbum.lazy';
import { EventPersonProvider } from '@apps/guest/shared/eventPerson/EventPersonProvider';
import { Suspense } from '@shared/core/react/Suspense';

const MediaCollectionLoader: FC<{ loadingSite: boolean }> = props => {
  const { loadingSite, children } = props;

  return <> {loadingSite ? <JoyLogoLoader loaderKey="media-collection-guest" /> : children} </>;
};

export const MediaCollection: FC<MediaCollectionProps> = props => {
  const { eventHandle } = props;

  const { data: eventData } = useMediaCollectionEventInfoQuery({
    variables: {
      eventHandle,
      eventDesignPurpose: EventDesignPurpose.live,
    },
    batchMode: 'fast',
    ssr: true,
    fetchPolicy: 'cache-first',
  });

  return (
    <>
      {eventData?.eventByName && (
        <MediaCollectionHead
          eventDisplayName={eventData.eventByName.info.eventDisplayName}
          eventType={eventData.eventByName.info.eventType}
          ownerFirstName={eventData.eventByName.info.ownerFirstName}
          ownerLastName={eventData.eventByName.info.ownerLastName}
          fianceeFirstName={eventData.eventByName.info.fianceeFirstName}
          imageUrl={eventData.eventByName.photo?.url}
          handle={eventHandle}
        />
      )}
      <EventDesignThemingProvider eventDesign={eventData?.eventByName?.eventDesign}>
        <MediaCollectionInner
          eventHandle={eventHandle}
          eventData={eventData}
        />
      </EventDesignThemingProvider>
    </>
  );
};

/**
 * Preloads the UploadAlbum chunk, Filestack library + credentials, and the
 * magic-token GQL query concurrently with font loading. Renders nothing —
 * placed as a sibling of MediaCollectionLoader so it runs even while
 * loadingFonts is true and the loader is still showing the spinner.
 */
const UploadRoutePreloader: FC = () => {
  const routePaths = useRoutePaths();
  const match = useRouteMatch<{ magicToken: string }>(routePaths.uploadAlbum.path);
  const isUploadMatch = !!match;

  usePrepareFilestackForUse(isUploadMatch);

  useEffect(() => {
    if (isUploadMatch) {
      LazyUploadAlbum.preload();
    }
  }, [isUploadMatch]);

  // Warm the Apollo cache so UploadAlbum gets an instant cache hit on mount.
  // On SSR-served requests the data is already in the cache; this acts as a
  // backup for client-side navigations and SSR cache misses.
  const token = match?.params.magicToken;
  useMediaCollectionByMagicTokenForUpdateQuery({
    variables: { token: token ?? '' },
    skip: !token,
    batchMode: 'fast',
  });

  return null;
};

const MediaCollectionInner: FC<{ eventHandle: string; eventData: MediaCollectionEventInfoQuery | undefined }> = ({ eventHandle, eventData }) => {
  const routePaths = useRoutePaths();
  const { loadingFonts } = useEventDesignThemingProvider();
  const isUploadRoute = !!useRouteMatch(routePaths.uploadAlbum.path);

  const [isInitialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    if (isInitialLoad) {
      setInitialLoad(false);
    }
  }, [isInitialLoad, loadingFonts]);

  // For /upload bypass the font-loading gate — the Filestack picker has its
  // own UI and does not depend on custom event fonts.
  const shouldShowLoader = !isUploadRoute && (loadingFonts || isInitialLoad);

  return (
    <>
      <UploadRoutePreloader />
      <MediaCollectionLoader loadingSite={shouldShowLoader}>
        <EventDesignJoyKitThemeOverridesProvider>
          <Flex
            width="100%"
            justifyContent="center"
          >
            <Flex
              width="100%"
              flexDirection="column"
              gap={7}
              maxWidth={pxToRem(1280)}
              paddingTop={7}
              paddingBottom={pxToRem(80)}
              paddingX={{ _: 7, sm4: 10 }}
            >
              <Flex
                flexDirection="column"
                width="100%"
                alignItems="center"
                gap={3}
              >
                <GuestSiteTypographyOverride override={display5ToEventTitleOverride}>
                  <TextV2
                    typographyVariant="display5"
                    color="mono14"
                    textAlign="center"
                  >
                    {eventData?.eventByName?.info.eventDisplayName}
                  </TextV2>
                </GuestSiteTypographyOverride>
              </Flex>
              {/*
                These routes' lazy chunks can suspend on a translation namespace that is not in the
                served i18n store — the language can change after hydration, leaving the store on the
                previous one. Without a boundary that suspend takes the whole tree to the error
                boundary in React 17 legacy mode.
              */}
              <Suspense fallback={<JoyLogoLoader loaderKey="media-collection-routes" />}>
                <Switch>
                  <Route
                    path={routePaths.uploadAlbum.path}
                    exact={true}
                    render={({ match }) => (
                      <LazyUploadAlbum
                        magicToken={match.params.magicToken}
                        eventId={eventData?.eventByName?.id || ''}
                      />
                    )}
                  />
                  <Route
                    path={routePaths.viewAlbums.path}
                    exact={true}
                    render={({ match }) => (
                      // Identity provider for the like flow — the standalone album route sits
                      // outside GuestSiteBody's EventPersonProvider, so reactions need their own.
                      <EventPersonProvider eventId={eventData?.eventByName?.id || ''}>
                        <LazyViewAlbum
                          magicToken={match.params.magicToken}
                          eventHandle={eventHandle}
                          eventId={eventData?.eventByName?.id || ''}
                        />
                      </EventPersonProvider>
                    )}
                  />
                  <Route
                    path={routePaths.albums.path}
                    render={({ match }) => (
                      <LazyAlbumsList
                        magicToken={match.params.magicToken}
                        eventHandle={eventHandle}
                      />
                    )}
                  />
                  <Route
                    path="*"
                    render={() => <Redirect to="/notfound" />}
                  />
                </Switch>
              </Suspense>
            </Flex>
          </Flex>
        </EventDesignJoyKitThemeOverridesProvider>
      </MediaCollectionLoader>
    </>
  );
};

export const MediaCollectionProvider: FC<MediaCollectionProps> = props => {
  return (
    <MediaCollectionTelemetryProvider>
      <MediaCollectionRouter eventHandle={props.eventHandle}>
        <MediaCollection {...props} />
      </MediaCollectionRouter>
    </MediaCollectionTelemetryProvider>
  );
};

MediaCollection.displayName = 'MediaCollection';
